import { z } from "zod";

declare const brand: unique symbol;
/** A string id tagged with the aggregate it belongs to, so a ChapterId cannot be passed as a BookId. */
export type Branded<T extends string> = string & { readonly [brand]: T };

export type BookId = Branded<"BookId">;
export type ChapterId = Branded<"ChapterId">;
export type JobId = Branded<"JobId">;
export type VoiceId = Branded<"VoiceId">;
export type SampleId = Branded<"SampleId">;
export type ScriptVersionId = Branded<"ScriptVersionId">;
export type StyleSheetVersionId = Branded<"StyleSheetVersionId">;
export type BlockId = Branded<"BlockId">;
export type AttemptId = Branded<"AttemptId">;
export type EstimateId = Branded<"EstimateId">;
export type InstructionId = Branded<"InstructionId">;
export type AudiobookId = Branded<"AudiobookId">;

function branded<T extends string>() {
  return z.uuid().transform((v) => v as Branded<T>);
}

export const BookIdSchema = branded<"BookId">();
export const ChapterIdSchema = branded<"ChapterId">();
export const JobIdSchema = branded<"JobId">();
export const VoiceIdSchema = branded<"VoiceId">();
export const SampleIdSchema = branded<"SampleId">();
export const ScriptVersionIdSchema = branded<"ScriptVersionId">();
export const StyleSheetVersionIdSchema = branded<"StyleSheetVersionId">();
export const BlockIdSchema = branded<"BlockId">();
export const AttemptIdSchema = branded<"AttemptId">();
export const EstimateIdSchema = branded<"EstimateId">();
export const InstructionIdSchema = branded<"InstructionId">();
export const AudiobookIdSchema = branded<"AudiobookId">();

/**
 * A Manuscript node id as the parsers mint them: DOCX p{i}, b{i}, b{i}_{k};
 * EPUB s{spine}n{counter}[#id]; Markdown m{counter}. Not a uuid.
 */
export const NodeIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_#.-]*$/);
export type NodeId = z.infer<typeof NodeIdSchema>;

/** sha256 hex, as the Estimate fingerprint, the render key, and content hashes travel. */
export const Sha256Schema = z.string().regex(/^[0-9a-f]{64}$/);

/** Casts a string that is already known to be a valid id (a DB row) into the branded type. */
export const asId = <T extends Branded<string>>(value: string): T => value as T;
